import * as React from "react";
import { styled } from "@mui/material/styles";
import {
  Dialog,
  Paper,
  Box,
  Tab,
  Tabs,
  TextField,
  InputAdornment,
} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import SearchIcon from "@mui/icons-material/Search";
import Typography from "@mui/material/Typography";

import TileIcon from "../../assets/images/TileIcon.svg";
import TileIconPink from "../../assets/images/TileIconPink.svg";
import {
  PromptDialogData,
  TOOL_PROMPT_CATEGORY_GROUPS,
  ALL_TOOL_PROMPT_CATEGORIES,
} from "../../sampledata/PromptDialogData";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import "./PromptDialog.scss";
import { inputUpdate } from "../../redux/slice/snackbar";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const CustomTabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`prompt-tabpanel-${index}`}
      aria-labelledby={`prompt-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ display: "flex", flexWrap: "wrap" }}>{children}</Box>
      )}
    </div>
  );
};

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `prompt-tab-${index}`,
    "aria-controls": `prompt-tabpanel-${index}`,
  };
}

// Debounce hook
function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = React.useState(value);

  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
}

export default function PromptDialog(props) {
  const {
    dialogOpen,
    handleDialogClose,
    userRole = "SALESREP",
    activeToolId = null,
  } = props;

  const [value, setValue] = React.useState(0);
  const [searchFocused, setSearchFocused] = React.useState(false);

  // search state
  const [searchText, setSearchText] = React.useState("");
  const debouncedSearch = useDebouncedValue(searchText, 300);

  const dispatch = useDispatch();

  const normalizedRole = (userRole || "").trim().toUpperCase();

  // Build categories & flattened list for search
  const { categories, questionsByCategory, allQuestions } =
    React.useMemo(() => {
      const byRole = PromptDialogData[normalizedRole] || {};

      const toolCategoryNames = TOOL_PROMPT_CATEGORY_GROUPS[activeToolId];

      const filteredByRole = toolCategoryNames
        ? Object.fromEntries(
            toolCategoryNames
              .filter((cat) => byRole[cat])
              .map((cat) => [cat, byRole[cat]])
          )
        : Object.fromEntries(
            Object.entries(byRole).filter(
              ([cat]) => !ALL_TOOL_PROMPT_CATEGORIES.includes(cat)
            )
          );

      const catNames = Object.keys(filteredByRole);

      const flattened = catNames.flatMap((cat) =>
        (filteredByRole[cat] || []).map((q) => ({
          category: cat,
          question: q,
        }))
      );

      return {
        categories: catNames,
        questionsByCategory: filteredByRole,
        allQuestions: flattened,
      };
    }, [normalizedRole, activeToolId]);

  const handleChange = (event, newValue) => setValue(newValue);

  const highlightText = (text, query) => {
    if (!query) return text;

    const safeText = String(text);
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const regex = new RegExp(`(${escapedQuery})`, "gi");

    return safeText.replace(
      regex,
      '<mark class="prompt-search-highlight">$1</mark>'
    );
  };

  const handleInputDataFromPrompt = (question) => {
    handleDialogClose();
    dispatch(inputUpdate(String(question)));
  };

  const trimmedQuery = debouncedSearch.trim();
  const isSearching = trimmedQuery.length > 0;

  const filteredResults = React.useMemo(() => {
    if (!isSearching) return [];
    const q = trimmedQuery.toLowerCase();
    return allQuestions.filter((item) =>
      (item.question || "").toLowerCase().includes(q)
    );
  }, [isSearching, trimmedQuery, allQuestions]);

  // Reset when dialog closes
  React.useEffect(() => {
    if (!dialogOpen) {
      setSearchText("");
      setValue(0);
    }
  }, [dialogOpen]);

  return (
    <BootstrapDialog
      className="prompt-dialog-parent"
      onClose={handleDialogClose}
      aria-labelledby="customized-dialog-title"
      open={dialogOpen}
      maxWidth="md"
      fullWidth
      InputProps={{
        sx: { maxWidth: "820px" },
        className: "prompt-dialog-parent-input-props",
      }}
    >
      <DialogTitle
        sx={{ m: 0, p: 2, fontSize: "16px", fontWeight: "bold" }}
        id="customized-dialog-title"
      >
        Prompt Library
      </DialogTitle>

      <Typography
        sx={{ mx: 2, fontSize: "12px", fontWeight: "400", color: "#6F6F6F" }}
      >
        Explore prompts to create, edit and get more done.
      </Typography>

      <IconButton
        aria-label="close"
        onClick={handleDialogClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>

      <Box sx={{ px: 2, pt: 2 }} className="prompt-library-search">
        <TextField
          fullWidth
          size="small"
          placeholder="Search keyword.."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          className="prompt-library-search-input"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{
                    fontSize: 18,
                    color: searchFocused ? "#E56800" : "#9E9E9E",
                  }}
                />
              </InputAdornment>
            ),
            endAdornment: searchText && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchText("")}
                  sx={{
                    color: searchFocused ? "#E56800" : "#9E9E9E",
                    "&:hover": { background: "transparent" },
                  }}
                >
                  <CancelOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {isSearching && (
          <Typography
            sx={{
              mt: 1,
              fontSize: "12px",
              color: "#6F6F6F",
            }}
          >
            {filteredResults.length} result
            {filteredResults.length === 1 ? "" : "s"}
          </Typography>
        )}
      </Box>

      {/* TABS (hidden when searching or only one category) */}
      {!isSearching && categories.length > 1 && (
        <Box className="prompt-dialog-box">
          <Tabs
            value={value}
            onChange={(_, v) => setValue(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            className="propmt-library-tabs"
            sx={{ mt: 3, ml: 2 }}
          >
            {categories.map((cat) => (
              <Tab key={cat} label={cat} />
            ))}
          </Tabs>
        </Box>
      )}

      <DialogContent className="prompt-dialog-content">
        <Box sx={{ width: "100%" }} className="prompt-library-tabs-content">
          {/* SEARCH RESULTS VIEW */}
          {isSearching ? (
            <Box
              sx={{ display: "flex", flexWrap: "wrap" }}
              className="prompt-library-tabs-panel"
            >
              {filteredResults.map((item, idx) => (
                <Paper
                  className="prompt-library-tabs-panel-paper"
                  variant="outlined"
                  key={`${item.category}-${idx}`}
                  sx={{ background: "#F3F3F3" }}
                  onClick={() => handleInputDataFromPrompt(item.question)}
                >
                  <img
                    src={TileIcon}
                    alt="TileIcon"
                    className="prompt-tile-icon"
                  />
                  <img
                    src={TileIconPink}
                    alt="TileIconPink"
                    className="prompt-tile-icon-pink"
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        whiteSpace: "normal",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: isSearching
                          ? highlightText(item.question, trimmedQuery)
                          : String(item.question),
                      }}
                    />
                  </Box>
                </Paper>
              ))}

              {filteredResults.length === 0 && (
                <Box sx={{ px: 1, py: 2 }}>
                  <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
                    No prompts found. Try a different keyword.
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            /* NORMAL TABS VIEW */
            categories.map((cat, idx) => (
              <CustomTabPanel
                key={cat}
                value={value}
                index={idx}
                className="prompt-library-tabs-panel"
              >
                {(questionsByCategory[cat] || []).map((question, qIdx) => (
                  <Paper
                    className="prompt-library-tabs-panel-paper"
                    variant="outlined"
                    key={qIdx}
                    sx={{ background: "#F3F3F3" }}
                    onClick={() => handleInputDataFromPrompt(question)}
                  >
                    <img
                      src={TileIcon}
                      alt="TileIcon"
                      className="prompt-tile-icon"
                    />
                    <img
                      src={TileIconPink}
                      alt="TileIconPink"
                      className="prompt-tile-icon-pink"
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {question}
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </CustomTabPanel>
            ))
          )}
        </Box>
      </DialogContent>
    </BootstrapDialog>
  );
}

PromptDialog.propTypes = {
  dialogOpen: PropTypes.bool.isRequired,
  handleDialogClose: PropTypes.func.isRequired,
  userRole: PropTypes.string, // "SALESREP" | "RBD" | "LEADERSHIP"
  activeToolId: PropTypes.string,
};
